import type { CampanaPublic } from '@app/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit2, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import { useInstancias } from '../../bandeja/api/instancias.api';
import { ChipInstancia } from '../../bandeja/components/Chips';
import { campanasApi } from '../api/campanas.api';
import { CampanaCrearDialog } from '../components/CampanaCrearDialog';
import { CampanaEditarDialog } from '../components/CampanaEditarDialog';

// Campañas como datos (Ciclo 1c R5): de dónde vienen los leads, cuántos trajo cada
// una, y el alta de la próxima sin deploy.
export function CampanasPage(): JSX.Element {
  const es = useI18n();
  const t = es.admin.campanas;
  const queryClient = useQueryClient();
  const [crearOpen, setCrearOpen] = useState(false);
  const [editando, setEditando] = useState<CampanaPublic | null>(null);
  const { instancias, porNombre } = useInstancias();

  const { data, isLoading, error } = useQuery({ queryKey: qk.campanas.all, queryFn: campanasApi.list });

  const activaMutation = useMutation({
    mutationFn: ({ id, activa }: { id: string; activa: boolean }) => campanasApi.update(id, { activa }),
    onSuccess: () => {
      toast.success(t.actualizada);
      void queryClient.invalidateQueries({ queryKey: qk.campanas.all });
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal));
    },
  });

  const fecha = (iso: string): string => new Date(iso).toLocaleDateString('es-AR');

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-2 flex items-center justify-between">
        <h1 className="font-titulos text-3xl font-bold text-text-marca">{t.titulo}</h1>
        <Button onClick={() => setCrearOpen(true)}>
          <Plus size={16} />
          {t.crear}
        </Button>
      </div>
      <p className="mb-6 max-w-3xl text-sm text-text-subtle">{t.intro}</p>

      {error && <Alert variant="error">{getErrorMessage(error, es.errores.generico.algoSalioMal)}</Alert>}
      {isLoading && <p className="text-sm text-text-subtle">{t.cargando}</p>}

      {data && data.campanas.length === 0 && <p className="text-sm text-text-faint">{t.vacio}</p>}

      {data && data.campanas.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-marca-neutro bg-surface">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-left text-xs uppercase tracking-wide text-text-subtle">
              <tr>
                <th className="px-3 py-2">{t.columnas.nombre}</th>
                <th className="px-3 py-2">{t.columnas.tipo}</th>
                <th className="px-3 py-2">{t.columnas.numeros}</th>
                <th className="px-3 py-2">{t.columnas.vigencia}</th>
                <th className="px-3 py-2">{t.columnas.textos}</th>
                <th className="px-3 py-2 text-right">{t.columnas.leads}</th>
                <th className="px-3 py-2">{t.columnas.activa}</th>
              </tr>
            </thead>
            <tbody>
              {data.campanas.map((c: CampanaPublic) => (
                <tr
                  key={c.id}
                  className={cn('border-t border-border-subtle', !c.activa && 'text-text-faint')}
                >
                  <td className="px-3 py-2 font-medium text-text-strong">{c.nombre}</td>
                  <td className="px-3 py-2">{t.tipos[c.tipo]}</td>
                  <td className="px-3 py-2">
                    <span className="flex flex-wrap gap-1">
                      {c.instancias.map((n) => (
                        <ChipInstancia
                          key={n}
                          instancia={porNombre[n] ?? { nombre: n, etiqueta: n, color: '0 0% 45%' }}
                        />
                      ))}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {fecha(c.vigenteDesde)} → {c.vigenteHasta ? fecha(c.vigenteHasta) : t.sinHasta}
                  </td>
                  <td className="px-3 py-2" title={c.textos.join('\n\n')}>
                    {t.verTextos.replace('{n}', String(c.textos.length))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.leads}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => activaMutation.mutate({ id: c.id, activa: !c.activa })}
                        disabled={activaMutation.isPending}
                      >
                        {c.activa ? t.desactivar : t.activar}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditando(c)}
                        aria-label={`${t.editar} ${c.nombre}`}
                      >
                        <Edit2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CampanaCrearDialog open={crearOpen} onOpenChange={setCrearOpen} instancias={instancias} />
      {editando && (
        <CampanaEditarDialog campana={editando} open onOpenChange={(o) => !o && setEditando(null)} />
      )}
    </div>
  );
}
